import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@core/authentication/auth.service';
import { ApplicationService, ClientService, SearchService } from '@core/services';
import { OiqFormInputComponent } from '@oiq-ui-sdk/form';
import { OiqGridTableComponent } from '@oiq-ui-sdk/grid';
import {
  OiqUiAlertDialogService,
  OiqUiDropdownComponent,
  OiqUiPanelComponent,
} from '@oiq-ui-sdk/ui';
import { StatusPipe } from '@v2/shared/pipes/status.pipe';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AdminRoutingModule } from 'src/app/admin/admin-routing.module';

export interface ClientSearchResult {
  id: string;
  name: string;
  iso: any;
  isoName?: string;
  date: string;
  createdDate?: string;
  expanded?: boolean;
  transactions?: ClientTransaction[];
}

export interface ClientTransaction {
  id: string;
  amount: string;
  iso: any; // Can be object or string 'N/A'
  fundedAmount?: number;
  date: string;
  createdDate?: string;
  status: string;
  declineReason?: string;
  menuOpen?: boolean;
}

@Component({
  selector: 'oiq-select-client-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    OiqFormInputComponent,
    OiqUiPanelComponent,
    OiqGridTableComponent,
    OiqUiDropdownComponent,
    StatusPipe,
    AdminRoutingModule,
  ],
  providers: [OiqUiAlertDialogService],
  templateUrl: './select-client-dialog.component.html',
  styleUrl: './select-client-dialog.component.scss',
})
export class SelectClientDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  nameInputRef = viewChild<OiqFormInputComponent>('nameInputRef');

  private authService = inject(AuthService);
  private searchService = inject(SearchService);
  private clientService = inject(ClientService);
  private applicationService = inject(ApplicationService);

  public dialogRef: DialogRef<any, SelectClientDialogComponent> = inject(DialogRef);

  @Inject(DIALOG_DATA)
  public data: {
    clientId?: number | string;
    whiteLabelId?: number;
    title?: string;
    appDialogsDisabled?: Signal<boolean>;
  } = inject(DIALOG_DATA);

  // Form and state management
  searchForm = new FormGroup({
    name: new FormControl(''),
  });

  get searchControl(): FormControl {
    return this.searchForm.get('name') as FormControl;
  }

  searching = signal<boolean>(false);
  clientResults = signal<ClientSearchResult[]>([]);
  expandedElement: any = null;
  applications: Array<any> = [];

  private destroy$ = new Subject<void>();

  appDialogsDisabled = computed(() => this.data?.appDialogsDisabled?.() ?? true);

  ngOnInit(): void {
    // If clientId is provided, fetch only that client
    if (this.data?.clientId) {
      const clientId =
        typeof this.data.clientId === 'string'
          ? parseInt(this.data.clientId, 10)
          : this.data.clientId;

      this.searching.set(true);
      this.clientService.getById(clientId).subscribe({
        next: client => {
          const clientResult: ClientSearchResult = {
            id: client.id.toString(),
            name: client.name,
            iso: client.iso,
            isoName: client.iso?.name || client.isoName,
            date: client.createdDate ? new Date(client.createdDate).toLocaleString() : 'N/A',
            createdDate: client.createdDate,
            expanded: false,
            transactions: [],
          };
          this.clientResults.set([clientResult]);
          this.searching.set(false);

          // Auto-expand the panel and load applications when clientId is provided
          setTimeout(() => {
            this.toggleExpansion(clientResult);
          }, 0);
        },
        error: error => {
          this.searching.set(false);
          console.error('Error fetching client by ID:', error);
        },
      });
      return;
    }

    // Handle tenant/whitelabel logic (same as client-search-dialog)
    let _whitelabelId = this.data?.whiteLabelId;
    const _tenantId = this.authService.getUser().tenantId;
    if (_tenantId == 1) {
      _whitelabelId = null;
    }

    // Set up search functionality with debounce (only if no clientId provided)
    this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(val => {
        if (val) {
          this.searching.set(true);
          this.searchService.performClientsTypeahead(val, null, _whitelabelId).subscribe({
            next: data => {
              const mappedResults = data.map(client => ({
                id: client.id,
                name: client.name,
                iso: client.iso,
                isoName: client.iso?.name || client.isoName,
                date: client.createdDate ? new Date(client.createdDate).toLocaleString() : 'N/A',
                createdDate: client.createdDate,
                expanded: false,
                transactions: [],
              }));
              this.clientResults.set(mappedResults);
              this.searching.set(false);
            },
            error: error => {
              this.searching.set(false);
              console.error('Error searching clients:', error);
            },
          });
        } else {
          this.clientResults.set([]);
        }
      });
  }

  ngAfterViewInit(): void {
    // Focus the search input when dialog opens
    this.nameInputRef()?.focusIn();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  selectClient(client: ClientSearchResult): void {
    this.dialogRef.close({
      client,
    });
  }

  selectApplication(application: any, client?: ClientSearchResult): void {
    // Find the full application object from the applications array

    this.dialogRef.close({
      application: application || null,
      client: client || null,
    });
  }

  onCreateNewApplication(): void {
    this.dialogRef.close({
      openNewApplicationDialog: true,
    });
  }

  toggleExpansion(client: ClientSearchResult): void {
    if (this.expandedElement === client) {
      this.expandedElement = null;
    } else {
      // Convert string ID to number if necessary for the API
      const clientId = typeof client.id === 'string' ? parseInt(client.id, 10) : client.id;

      this.applicationService.listApplicationsByClientId(clientId).subscribe(data => {
        this.expandedElement = client;
        this.applications = data.content;

        // Format applications as transactions for our display model
        client.transactions = data.content.map(app => ({
          id: app.id,
          amount: app.fundedAmount ? `$${app.fundedAmount.toLocaleString()}` : 'N/A',
          fundedAmount: app.fundedAmount,
          iso: app.iso ? app.iso : 'N/A',
          date: app.createdDate ? new Date(app.createdDate).toLocaleDateString() : 'N/A',
          createdDate: app.createdDate,
          status: app.status,
          declineReason: app.declineReason,
          menuOpen: false,
        }));
      });
    }
  }

  handlePanelEvent(event: any, client: any): void {
    switch (event.type) {
      case 'close':
      case 'open':
        this.toggleExpansion(client);
        break;
      default:
        break;
    }
  }
}
